<template>
  <div class="survey-emotion-question">
    <div>
      <p class="survey-emotion-question__title">{{ title }}</p>
      <p class="survey-emotion-question__counter">
        Выбрано {{ selectedCount }} из {{ max }}
      </p>
    </div>


    <div class="survey-emotion-question__grid">
      <div
          v-for="group in emotionGroups"
          :key="group.id"
          class="survey-emotion-question__row"
      >
        <button
            v-for="(label, emotionIndex) in group.emotions"
            :key="label"
            type="button"
            class="survey-emotion-question__chip"
            :class="{ 'survey-emotion-question__chip--selected': isSelected(group.id, emotionIndex) }"
            :style="chipStyle(group, emotionIndex)"
            @click="toggleEmotion(group.id, emotionIndex)"
        >
          {{ label }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { EMOTION_GROUPS } from '@/feature/surveys/constants/emotionOptions';

export default {
  name: 'SurveyEmotionQuestion',
  props: {
    title: {
      type: String,
      required: true,
    },
    min: {
      type: Number,
      default: 2,
    },
    max: {
      type: Number,
      default: 5,
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const emotionGroups = EMOTION_GROUPS;

    const selectedCount = computed(() => props.modelValue.length);

    const emotionIdByText = computed(() => {
      return props.options.reduce((result, emotion) => {
        const text = emotion.text ?? emotion.value ?? emotion.name;

        if (text && emotion.id !== undefined && emotion.id !== null) {
          result[text] = emotion.id;
        }

        return result;
      }, {});
    });

    const emotionId = (spectrumId, emotionIndex) => `${spectrumId}-${emotionIndex}`;

    const getEmotionValue = (spectrumId, emotionIndex) => {
      const group = emotionGroups.find((item) => item.id === spectrumId);
      const text = group?.emotions?.[emotionIndex];

      return emotionIdByText.value[text] ?? emotionId(spectrumId, emotionIndex);
    };

    const isSelected = (spectrumId, emotionIndex) => {
      return props.modelValue.includes(getEmotionValue(spectrumId, emotionIndex));
    };

    const chipStyle = (group, emotionIndex) => {
      const color = group.colors[emotionIndex];
      const selected = isSelected(group.id, emotionIndex);

      if (selected) {
        return {
          '--emotion-chip-background': color,
          '--emotion-chip-border': color,
          '--emotion-chip-text': '#3f4a4a',
        };
      }

      return {
        '--emotion-chip-background': '#ffffff',
        '--emotion-chip-border': color,
        '--emotion-chip-text': '#5f6268',
      };
    };

    const toggleEmotion = (spectrumId, emotionIndex) => {
      const id = getEmotionValue(spectrumId, emotionIndex);
      const next = [...props.modelValue];
      const index = next.indexOf(id);

      if (index >= 0) {
        next.splice(index, 1);
      } else if (next.length < props.max) {
        next.push(id);
      }

      emit('update:modelValue', next);
    };

    return {
      emotionGroups,
      selectedCount,
      isSelected,
      chipStyle,
      toggleEmotion,
    };
  },
};
</script>

<style scoped lang="scss">
$survey-answer-bg: #00000005;

.survey-emotion-question {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__title {
    margin: 0 0 8px;
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 120%;
  }

  &__counter {
    margin: -4px 0 0;
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 120%;
    color: rgba(0, 0, 0, 0.45);
  }

  &__grid {
    display: flex;
    flex-direction: column;
    padding: 16px;
    gap: 8px;
    border-radius: 8px;
    background: $survey-answer-bg;
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__chip {
    --emotion-chip-background: #ffffff;
    --emotion-chip-border: var(--neutral-border);
    --emotion-chip-text: rgba(0, 0, 0, 0.65);

    min-height: 36px;
    padding: 10px 12px;
    border: 1px solid var(--emotion-chip-border);
    border-radius: 999px;
    background: var(--emotion-chip-background);
    font-size: 14px;
    line-height: 120%;
    font-weight: 600;
    color: var(--emotion-chip-text);
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s, color 0.2s;

    &--selected {
      font-weight: 600;
      color: rgba(0, 0, 0, 0.88);
    }

    &:hover:not(&--selected) {
      background: var(--emotion-chip-background);
    }
  }
}
</style>
